import { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { contact } from '../lib/api';
import { trackContactSalesFormSubmitted } from '../lib/analytics';

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
  source?: 'pricing' | 'paywall' | 'upgrade-modal';
}

const TEAM_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'] as const;

export default function ContactSalesModal({ isOpen, onClose, source }: ContactSalesModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // Honeypot
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const submitMutation = useMutation({
    mutationFn: () => contact.submitSalesForm({
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      teamSize: teamSize as typeof TEAM_SIZES[number],
      message: message.trim(),
      source,
      website,
    }),
    onSuccess: () => {
      trackContactSalesFormSubmitted(teamSize, source);
      setSubmitted(true);
    },
  });

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Reset on close
  const handleClose = useCallback(() => {
    setName('');
    setEmail('');
    setCompany('');
    setTeamSize('');
    setMessage('');
    setWebsite('');
    setErrors({});
    setSubmitted(false);
    submitMutation.reset();
    onClose();
  }, [onClose, submitMutation]);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    if (!name.trim() || name.trim().length > 100) {
      newErrors.name = 'Name is required (max 100 characters)';
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'A valid email address is required';
    }
    if (!company.trim() || company.trim().length > 200) {
      newErrors.company = 'Company name is required (max 200 characters)';
    }
    if (!teamSize) {
      newErrors.teamSize = 'Please select a team size';
    }
    if (!message.trim() || message.trim().length > 2000) {
      newErrors.message = 'Message is required (max 2000 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    submitMutation.mutate();
  }

  function getErrorMessage(): string | null {
    if (!submitMutation.isError) return null;
    const error = submitMutation.error as { status?: number; message?: string };
    if (error.status === 429) return 'Too many requests. Please try again later.';
    return error.message || 'Something went wrong. Please try again.';
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md transform transition-all">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {submitted ? (
            /* Success view */
            <div className="px-6 py-10 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Thanks for reaching out!</h3>
              <p className="text-sm text-gray-600 mb-6">
                We'll be in touch within 1 business day.
              </p>
              <button onClick={handleClose} className="btn-primary w-full">
                Close
              </button>
            </div>
          ) : (
            /* Form view */
            <form onSubmit={handleSubmit} noValidate className="px-6 pt-8 pb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Contact Sales</h3>
              <p className="text-sm text-gray-500 mb-6">Tell us about your team and we'll get back to you.</p>

              {/* Error banner */}
              {submitMutation.isError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <p className="text-sm text-red-700">{getErrorMessage()}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor="contact-name" className="label">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`input ${errors.name ? 'border-red-300' : ''}`}
                    placeholder="Your name"
                    maxLength={100}
                  />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="contact-email" className="label">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`input ${errors.email ? 'border-red-300' : ''}`}
                    placeholder="you@company.com"
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="contact-company" className="label">Company</label>
                  <input
                    id="contact-company"
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className={`input ${errors.company ? 'border-red-300' : ''}`}
                    placeholder="Company name"
                    maxLength={200}
                  />
                  {errors.company && <p className="text-xs text-red-600 mt-1">{errors.company}</p>}
                </div>

                {/* Team Size */}
                <div>
                  <label htmlFor="contact-team-size" className="label">Team size</label>
                  <select
                    id="contact-team-size"
                    value={teamSize}
                    onChange={(e) => setTeamSize(e.target.value)}
                    className={`input ${errors.teamSize ? 'border-red-300' : ''}`}
                  >
                    <option value="">Select team size</option>
                    {TEAM_SIZES.map((size) => (
                      <option key={size} value={size}>{size} people</option>
                    ))}
                  </select>
                  {errors.teamSize && <p className="text-xs text-red-600 mt-1">{errors.teamSize}</p>}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="contact-message" className="label">Message</label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className={`input ${errors.message ? 'border-red-300' : ''}`}
                    placeholder="Tell us about your requirements..."
                    rows={4}
                    maxLength={2000}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
                    <p className="text-xs text-gray-400 ml-auto">{message.length}/2000</p>
                  </div>
                </div>

                {/* Honeypot — hidden from users */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitMutation.isPending}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitMutation.isPending ? 'Sending...' : 'Send Message'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Enterprise plans are managed separately
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
